/**
 * The reasons that block two or more of a record's commands at once (a draft quote another person is editing blocks
 * every command that changes the draft quote). A page says each of these once above the commands, not under every
 * disabled button; a reason that blocks one command stays under that command.
 */
import type { Command } from "./contract";

export function sharedReasonsOf(commands: readonly Command[]): string[] {
  const counts = new Map<string, number>();
  for (const command of commands) {
    if (!command.enabled && command.reason)
      counts.set(command.reason, (counts.get(command.reason) ?? 0) + 1);
  }
  return [...counts].filter(([, count]) => count > 1).map(([reason]) => reason);
}

/**
 * What the record header says about the commands that cannot run (Ben, 27 Oct 2026). When the record's goal or next
 * step (`lead`, `headerBlockedLeadOf`) cannot run and has a reason, that reason is said above the buttons so the person sees
 * what stops the record; when it blocks several commands it is said once as that reason alone, without the command's
 * label. Any other blocked command is not the record's next step (Stop trading… on a trading company that can still be
 * updated, Ben 4 Nov), so it is folded under "Not available now": `folded` lists them, `foldedShared` the shared
 * reasons not already said above, and `sharedReasons` every shared reason, so a fold row leaves out a reason said once
 * elsewhere.
 */
export interface BlockedLayout {
  said: { label: string | null; reason: string } | null;
  folded: Command[];
  sharedReasons: string[];
  foldedShared: string[];
}

export function blockedLayoutOf(
  commands: readonly Command[],
  lead: Command | null,
): BlockedLayout {
  const blocked = commands.filter((c) => !c.enabled);
  const sharedReasons = sharedReasonsOf(blocked);
  const first = lead && blocked.includes(lead) ? lead : null;
  if (!first?.reason)
    return {
      said: null,
      folded: blocked,
      sharedReasons,
      foldedShared: sharedReasons,
    };
  if (sharedReasons.includes(first.reason))
    return {
      said: { label: null, reason: first.reason },
      folded: blocked,
      sharedReasons,
      foldedShared: sharedReasons.filter((reason) => reason !== first.reason),
    };
  return {
    said: { label: first.label, reason: first.reason },
    folded: blocked.filter((c) => c !== first),
    sharedReasons,
    foldedShared: sharedReasons,
  };
}
